import React, { useState, useMemo, useEffect } from "react";
import {
  Row,
  Col,
  Typography,
  Card,
  Table,
  Tag,
  Space,
  Modal,
} from "antd";
import {
  UserOutlined,
  RiseOutlined,
  CrownOutlined,
  RocketOutlined,
  TrophyOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import moment from "moment";
import axios from "axios";
import ENVIRONMENT from "../../../../environments";
import styles from "./AdminAICharts.module.css";
import FreeTrialsChart from "./FreeTrialsChart";
import ConversionsChart from "./ConversionsChart";
import ImageGenerationsChart from "./ImageGenerationsChart";
import DateRangePicker from "../../DateRangePicker/DateRangePicker";
import useTableFilter from "../../UseTableFilter/useTableFilter";

const { Title, Text } = Typography;
const DATE_FORMAT = "YYYY/MM/DD";

const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

function AdminAICharts() {
  const chartStyle = useMemo(() => ({ 
    padding: 20, 
    background: "#fff", 
    borderRadius: 8 
  }), []);

  const [dateRange, setDateRange] = useState({
    start: moment().subtract(6, 'days'),
    end: moment()
  });

  const [getColumnSearchProps] = useTableFilter();

  const handleDateChange = (dateValues) => {
    if (dateValues && dateValues[0] && dateValues[1]) {
      setDateRange({
        start: dateValues[0],
        end: dateValues[1]
      });
    }
  };

  // State for subscription data
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Modal and User list state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [allUsersData, setAllUsersData] = useState([]);
  const [isUsersLoading, setIsUsersLoading] = useState(false);

  // Companies state
  const [companiesData, setCompaniesData] = useState([]);
  const [isCompaniesLoading, setIsCompaniesLoading] = useState(false);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [selectedCompanySubscription, setSelectedCompanySubscription] = useState(null);
  const [companyDetailsList, setCompanyDetailsList] = useState([]);

  // Icon mapping for subscription types
  const getIconForSubscription = (typeName) => {
    const lowerName = typeName.toLowerCase();
    if (lowerName.includes('free trial')) return <UserOutlined />;
    if (lowerName.includes('starter')) return <RocketOutlined />;
    if (lowerName.includes('growth')) return <RiseOutlined />;
    if (lowerName.includes('pro')) return <CrownOutlined />;
    if (lowerName.includes('basic') || lowerName.includes('bundle')) return <TrophyOutlined />;
    return <UserOutlined />;
  };

  // Fetch active users data
  const fetchActiveUsersData = async () => {
    try {
      setIsLoading(true);
      const payload = {
        action: "get_active_users_count"
      };

      const response = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, payload);
      const data = response.data || [];
      
      // Transform API data to table format
      const transformedData = data.map((item, index) => ({
        key: index.toString(),
        subscription: item.type_name,
        activeUsers: parseInt(item.count, 10),
        icon: getIconForSubscription(item.type_name),
      }));

      setSubscriptionData(transformedData);
    } catch (error) {
      console.error("Error fetching active users data:", error);
      setSubscriptionData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch all users for all subscriptions once
  const fetchAllUsersData = async () => {
    try {
      setIsUsersLoading(true);
      const payload = {
        action: 'get_users_filter_by_subscription_type'
      };

      const response = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, payload);
      const data = response.data || [];
      
      setAllUsersData(data);
    } catch (error) {
      console.error("Error fetching all subscription users:", error);
      setAllUsersData([]);
    } finally {
      setIsUsersLoading(false);
    }
  };

  const handleCellClick = (record) => {
    setSelectedSubscription(record.subscription);
    setIsModalOpen(true);
    
    const filtered = allUsersData.filter(user => 
      user.subscription_name === record.subscription
    );
    setUsersList(filtered);
  };

  // Fetch companies data
  const fetchCompaniesData = async () => {
    try {
      setIsCompaniesLoading(true);
      const payload = {
        action: "get_companies_by_subscription"
      };

      const response = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, payload);
      const data = response.data || [];
      
      const transformedData = data.map((item, index) => ({
        key: index.toString(),
        subscription: item.type_name,
        count: parseInt(item.count, 10),
        details: item.details || [],
        icon: getIconForSubscription(item.type_name),
      }));

      setCompaniesData(transformedData);
    } catch (error) {
      console.error("Error fetching companies data:", error);
      setCompaniesData([]);
    } finally {
      setIsCompaniesLoading(false);
    }
  };

  const handleCompanyCellClick = (record) => {
    setSelectedCompanySubscription(record.subscription);
    setCompanyDetailsList(record.details);
    setIsCompanyModalOpen(true);
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchActiveUsersData();
    fetchAllUsersData();
    fetchCompaniesData();
  }, []);

  const columns = [
    {
      title: 'Subscription Plan',
      dataIndex: 'subscription',
      key: 'subscription',
      onCell: (record) => ({
        onClick: () => handleCellClick(record),
        style: { cursor: 'pointer' }
      }),
      render: (text, record) => (
        <Space>
          <span style={{ fontSize: '16px', color: '#1890ff' }}>
            {record.icon}
          </span>
          <Text strong style={{ fontSize: '14px' }}>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Active Users',
      dataIndex: 'activeUsers',
      key: 'activeUsers',
      align: 'center',
      onCell: (record) => ({
        onClick: () => handleCellClick(record),
        style: { cursor: 'pointer' }
      }),
      render: (value) => (
        <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
          {value.toLocaleString()}
        </Text>
      ),
    },
  ];

  const userTableColumns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      ...getColumnSearchProps('username'),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ...getColumnSearchProps('email'),
    },
    {
      title: 'Profile Name',
      dataIndex: 'profile_name',
      key: 'profile_name',
      ...getColumnSearchProps('profile_name'),
    },
    {
      title: 'Company Name',
      dataIndex: 'company_display_name',
      key: 'company_display_name',
      ...getColumnSearchProps('company_display_name'),
    },
    {
      title: 'Subscription Start Date',
      dataIndex: 'subscription_start_time',
      key: 'subscription_start_time',
      render: (text) => text,
    },
    {
      title: 'Subscription Renewal Date',
      dataIndex: 'subscription_end_time',
      key: 'subscription_end_time',
      render: (text) => text,
    }
  ];

  const companyColumns = [
    {
      title: 'Subscription Plan',
      dataIndex: 'subscription',
      key: 'subscription',
      onCell: (record) => ({
        onClick: () => handleCompanyCellClick(record),
        style: { cursor: 'pointer' }
      }),
      render: (text, record) => (
        <Space>
          <span style={{ fontSize: '16px', color: '#1890ff' }}>
            {record.icon}
          </span>
          <Text strong style={{ fontSize: '14px' }}>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Active Companies',
      dataIndex: 'count',
      key: 'count',
      align: 'center',
      onCell: (record) => ({
        onClick: () => handleCompanyCellClick(record),
        style: { cursor: 'pointer' }
      }),
      render: (value) => (
        <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
          {value.toLocaleString()}
        </Text>
      ),
    },
  ];

  const companyDetailsColumns = [
    {
      title: 'Company ID',
      dataIndex: 'company_id',
      key: 'company_id',
    },
    {
      title: 'Display Name',
      dataIndex: 'company_display_name',
      key: 'company_display_name',
      ...getColumnSearchProps('company_display_name'),
    },
    {
      title: 'Company Domain',
      dataIndex: 'company_domain',
      key: 'company_domain',
      ...getColumnSearchProps('company_domain'),
    },
    {
      title: 'Subscription Start Date',
      dataIndex: 'subscription_start_time',
      key: 'subscription_start_time',
      render: (text) => text  ,
    },
    {
      title: 'Subscription Renewal Date',
      dataIndex: 'subscription_end_time',
      key: 'subscription_end_time',
      render: (text) => text  ,
    },
  ];

  return (
    <Row gutter={[24, 24]}>
      <Col span={24}>
        <Card style={{ ...chartStyle, marginBottom: 0 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                Active Subscriptions Overview
              </Title>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                Real-time subscription and user metrics
              </Text>
            </Col>
          </Row>
        </Card>
      </Col>

      <Col span={12}>
        <Card style={chartStyle}>
          <Title level={5} style={{ marginTop: 0 }}>
            Active Users by Subscription
          </Title>
          <Table
            columns={columns}
            dataSource={subscriptionData}
            pagination={false}
            size="middle"
            style={{ marginTop: 16 }}
            loading={{ indicator: antIcon, spinning: isLoading }}
          />
        </Card>
      </Col>

      <Col span={12}>
        <Card style={chartStyle}>
          <Title level={5} style={{ marginTop: 0 }}>
            Active Companies by Subscription
          </Title>
          <Table
            columns={companyColumns}
            dataSource={companiesData}
            pagination={false}
            size="middle"
            style={{ marginTop: 16 }}
            loading={{ indicator: antIcon, spinning: isCompaniesLoading }}
          />
        </Card>
      </Col>

      <FreeTrialsChart 
        chartStyle={chartStyle} 
        dateRange={dateRange}
        onDateChange={handleDateChange}
      />

      <ConversionsChart 
        chartStyle={chartStyle} 
        dateRange={dateRange}
        onDateChange={handleDateChange}
      />

      <ImageGenerationsChart 
        chartStyle={chartStyle} 
        dateRange={dateRange}
        onDateChange={handleDateChange}
      />

      <Modal
        title={`Users having ${selectedSubscription || '-'} Subscription`}
        visible={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setUsersList([]);
        }}
        footer={null}
        width={1000}
        destroyOnClose={true}
        centered
      >
        <Table
          columns={userTableColumns}
          dataSource={usersList}
          loading={{ indicator: antIcon, spinning: isUsersLoading }}
          pagination={false}
          scroll={{ y: 400 }}
          size="middle"
        />
      </Modal>

      <Modal
        title={`Companies having ${selectedCompanySubscription || '-'} Subscription`}
        visible={isCompanyModalOpen}
        onCancel={() => {
          setIsCompanyModalOpen(false);
          setCompanyDetailsList([]);
        }}
        footer={null}
        width={1000}
        destroyOnClose={true}
        centered
      >
        <Table
          columns={companyDetailsColumns}
          dataSource={companyDetailsList}
          pagination={false}
          scroll={{ y: 400 }}
          size="middle"
        />
      </Modal>
    </Row>
  );
}

export default AdminAICharts;